import { useCallback, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';
import useGetXPSummaryReportByUser from '@/hooks/useGetXPSummaryReportByUser';
import { usePlanLocations } from '@/hooks/usePlanLocations';

import { arrayToCommaString } from '@/utils/arrayToCommaString';
import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { convertDateForReports } from '@/utils/date';
import { itemByLanguage } from '@/utils/language';
import { getCompletionDate } from '@/utils/reports';
import { getTimeString } from '@/utils/time';

import Constants from '@/constants';

import { processUserName } from '@/services/modelHelper';

import { PrintReportSubject } from './state';

import {
  ComplianceCourseEnrollmentDto,
  generateCourseReportOptions,
  getPlanReportOptions,
  getUserTrainingPlansOptions,
  PersonCourseReportDto,
  UserPlanStatus,
} from '@/_generated/api-clients/xp';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import { TranslationKey } from '@/i18n';

type CompliancePlanReportRow = PersonCourseReportDto &
  Pick<ComplianceCourseEnrollmentDto, 'duration'>;

interface UsePrintReportDataParams {
  subject: PrintReportSubject | null;
}

/**
 * Unified hook to fetch, map, and generate print report data.
 * Handles both plan reports (compliance and non-compliance) and team member reports.
 */
export const usePrintReportData = ({ subject }: UsePrintReportDataParams) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const locationsWithAtLeastLeaderPermission =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const isPlanPrint = subject?.type === 'plan';
  const isTeamMemberPrint = subject?.type === 'teamMember';
  const isCompliance = isPlanPrint && subject.plan.type === 'COMPLIANCE';
  const isLeaderOrOperator = !!locationsWithAtLeastLeaderPermission.length;

  // Lazy fetch state - queries stay disabled until startFetching is called
  const [shouldFetch, setShouldFetch] = useState(false);
  const [_selectedLocations, setSelectedLocations] = useState<string[] | null>(
    null,
  );

  const subjectId = isPlanPrint ? subject.plan.id : subject?.id;

  // Reset fetch state when subject changes
  useEffect(() => {
    setShouldFetch(false);
  }, [subjectId]);

  const startFetching = useCallback((locations: string[]) => {
    setSelectedLocations(locations);
    setShouldFetch(true);
  }, []);

  // Plan locations callback - compute on demand
  const getLocationsByPlan = usePlanLocations();
  const printLocations = isPlanPrint ? getLocationsByPlan(subject.plan) : [];

  const { xpCompliancePlans: inHouseCompliancePlansFF } = useFlags();

  const plan = isPlanPrint ? subject.plan : null;

  // --- Plan Report: Compliance Data Fetching ---
  // Fetch all data for leader's locations, filter by user selection at generation time
  const {
    data: complianceReportData,
    isFetching: isComplianceFetching,
    error: complianceError,
  } = useQuery({
    ...generateCourseReportOptions({
      path: { courseId: plan?.id ?? 'no-id' },
      query: {
        location: plan ? getLocationsByPlan(plan) : [],
      },
    }),
    enabled:
      shouldFetch &&
      isPlanPrint &&
      isCompliance &&
      !inHouseCompliancePlansFF &&
      isLeaderOrOperator &&
      !!locationsWithAtLeastLeaderPermission.length,
  });

  // --- Plan Report: Non-Compliance Data Fetching ---
  const licenseeLocation =
    (userIsLicenseeStaff || isLicenseeUser) && activeLicenseeLocation?.number;

  const {
    data: planReportAndAssignments,
    isFetching: isPlanReportFetching,
    error: planReportError,
  } = useQuery({
    ...getPlanReportOptions({
      path: { planId: isPlanPrint ? subject.plan.id : 'no-id' },
      query: {
        locationNumbers: licenseeLocation ? [licenseeLocation] : undefined,
      },
    }),
    enabled:
      shouldFetch && isPlanPrint && (!isCompliance || inHouseCompliancePlansFF),
  });

  // --- Team Member Report Data Fetching ---
  const teamMemberId = isTeamMemberPrint ? subject.id : '';
  const shouldUseOldFetch = !!teamMemberId;

  const {
    data: teamMemberOldReportsData,
    isFetching: isTeamMemberOldFetching,
    error: teamMemberOldError,
  } = useQuery({
    ...getUserTrainingPlansOptions({
      path: { adId: teamMemberId },
    }),
    enabled: shouldFetch && shouldUseOldFetch,
  });

  const {
    xpReportsData: teamMemberXpData,
    isFetchingXpReport: isTeamMemberXpFetching,
    errorXpReports: teamMemberXpError,
  } = useGetXPSummaryReportByUser(
    teamMemberId,
    !shouldFetch || !shouldUseOldFetch,
  );

  // --- Derived Data ---

  // Compliance plan report data mapped for printing
  const complianceReportRows = (complianceReportData?.userData ??
    []) as CompliancePlanReportRow[];

  const complianceReportToPrint = [...complianceReportRows]
    .sort((a, b) => (a.firstName ?? '').localeCompare(b.firstName ?? ''))
    .map(report => ({
      [Constants.REPORT_TABLE.NAME]: `${report.firstName} ${report.lastName}`,
      [Constants.REPORT_TABLE.DATE_OF_BIRTH]:
        report.birthDate ?? t('Generic.na'),
      [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: report.duration
        ? getTimeString(Math.ceil(report.duration / 60))
        : t('Generic.na'),
      [Constants.REPORT_TABLE.COMPLETION_DATE]: getCompletionDate(report),
      [Constants.REPORT_TABLE.EXPIRATION_DATE]:
        report.certificate?.expiration &&
        report.courseStatus !==
          Constants.LEARN_UPON_TRAINING_PLAN_STATUSES.failed
          ? convertDateForReports(report.certificate.expiration)
          : t('Generic.na'),
    }));

  // Non-compliance plan report data - filter and map at report generation time
  const planReportData = planReportAndAssignments?.planUserReports;

  // Build non-compliance report data, filtering by selected locations
  // NOTE: In-house compliance plans are technically "non-compliance" based on the logic that Compliance plans are different. We should be able to delete the complianceReportToPrint logic out from here post-LearnUpon
  const buildNonComplianceReport = (
    locations: string[],
  ): Record<string, string | undefined>[] => {
    if (!planReportData) return [];

    const locationCount = printLocations.length;

    return uniqBy(
      planReportData.filter(entry =>
        locationCount > 1 && entry.user?.locations.length
          ? entry.user.locations.some(location => locations.includes(location))
          : entry,
      ),
      entry => entry.user?.id,
    )
      .sort((a, b) =>
        processUserName(a.user).localeCompare(processUserName(b.user)),
      )
      .map(entry => ({
        [Constants.REPORT_TABLE.NAME]: processUserName(entry.user),
        [Constants.REPORT_TABLE.DATE_OF_BIRTH]:
          entry.user?.birthDate ?? t('Generic.na'),
        [Constants.REPORT_TABLE.ITEMS_COMPLETED]: `${
          entry.stepsComplete ?? 0
        }/${planReportAndAssignments.totalSteps}`,
        [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: getTimeString(
          Math.ceil((entry.timeTakenInSeconds ?? 0) / 60),
        ),
        [Constants.REPORT_TABLE.STATUS]: entry.status
          ? t(
              `TrainingPlans.statusOptions.${
                Constants.TRAINING_PLANS_STATUSES[entry.status]
              }`,
            )
          : t('Generic.na'),
        [Constants.REPORT_TABLE.COMPLETION_DATE]: (() => {
          const date =
            entry.latestCompletionReport?.completionDate ??
            entry.completionDate;
          return date ? convertDateForReports(date) : t('Generic.na');
        })(),
        id: planReportAndAssignments.id,
        [Constants.REPORT_TABLE.EXPIRATION_DATE]: (() => {
          const date =
            entry.latestCompletionReport?.expirationDate ??
            entry.certificateExpirationDate;
          return date ? convertDateForReports(date) : t('Generic.na');
        })(),
      }));
  };

  // Team member plans data combined
  const { xpPlans, courses } = (() => {
    const coursesEnrollments =
      teamMemberOldReportsData?.coursesResults?.[0]?.enrollments;
    const allCourses: ComplianceCourseEnrollmentDto[] = coursesEnrollments
      ? Object.values(coursesEnrollments)
      : [];

    const plans: UserPlanStatus[] = (teamMemberXpData?.planStatus ?? []).filter(
      plan => plan.planType !== 'COMPLIANCE',
    );

    return {
      xpPlans: plans,
      courses: isLicenseeUser ? [] : allCourses,
    };
  })();

  // --- Team Member Mapping Helpers ---

  const getTeamMemberTimeString = (time: number) => {
    const timeSpentHrs = Math.floor(time / 60);
    const timeSpentMins = time % 60;
    return `${
      timeSpentHrs > 0 ? `${timeSpentHrs} ${t('Generic.hour')}` : ''
    } ${timeSpentMins} ${t('Generic.mins')}`;
  };

  const getCourseCompletedDate = (course: ComplianceCourseEnrollmentDto) => {
    const courseName = itemByLanguage(course.courseName) ?? '';
    const courseCompletedDate = course.completedDate;
    const courseStatus = course.status;

    if (!courseName || !courseCompletedDate) {
      return t('Generic.na');
    }

    const coursePassed =
      courseStatus === Constants.LEARN_UPON_TRAINING_PLANS.PASSED;
    const isFoodSafety = courseName.includes(Constants.FOOD_SAFETY_COURSE_NAME);

    if (!isFoodSafety || coursePassed) {
      return convertDateForReports(courseCompletedDate);
    }

    return t('Generic.na');
  };

  const mapComplianceCourseToReport = (
    course: ComplianceCourseEnrollmentDto,
  ) => {
    const courseName = itemByLanguage(course.courseName) ?? '';
    const courseExpire = course.certificate?.expiration;
    const courseStatus = course.status;
    const notFailed =
      courseStatus !== Constants.LEARN_UPON_TRAINING_PLAN_STATUSES.failed;

    return {
      [Constants.REPORT_TABLE.PLAN]: courseName,
      [Constants.REPORT_TABLE.ITEMS_COMPLETED]: `${course.percentComplete}%`,
      [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: course.duration
        ? getTimeString(Math.ceil(course.duration / 60))
        : t('Generic.na'),
      [Constants.REPORT_TABLE.STATUS]: (() => {
        const learnUponStatuses = Constants.LEARN_UPON_TRAINING_PLAN_STATUSES;
        const statusKey = course.status;
        const translationSlug =
          statusKey && statusKey in learnUponStatuses
            ? (learnUponStatuses as Record<string, string>)[statusKey]
            : null;
        return translationSlug
          ? t(
              `TrainingPlans.statusOptions.${translationSlug}` as TranslationKey,
            )
          : t('Generic.na');
      })(),
      [Constants.REPORT_TABLE.COMPLETION_DATE]: getCourseCompletedDate(course),
      [Constants.REPORT_TABLE.EXPIRATION_DATE]:
        courseExpire && notFailed
          ? convertDateForReports(courseExpire)
          : t('Generic.na'),
    };
  };

  const mapXpStorePlanToReport = (plan: UserPlanStatus) => {
    const planName = itemByLanguage(plan.planName) ?? '';
    const planTimeSpent = getTeamMemberTimeString(
      Math.ceil(plan.timeTakenInSeconds / 60),
    );
    return {
      [Constants.REPORT_TABLE.PLAN]: planName,
      [Constants.REPORT_TABLE.ITEMS_COMPLETED]:
        `${plan.completedSteps}/${plan.totalSteps}`,
      [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: planTimeSpent,
      [Constants.REPORT_TABLE.STATUS]: t(
        `TrainingPlans.statusOptions.${
          Constants.TRAINING_PLANS_STATUSES[plan.planStatus]
        }`,
      ),
      [Constants.REPORT_TABLE.COMPLETION_DATE]: plan.completionDate
        ? convertDateForReports(plan.completionDate)
        : t('Generic.na'),
      [Constants.REPORT_TABLE.EXPIRATION_DATE]: t('Generic.na'),
    };
  };

  // --- Loading & Error State ---
  const isFetching = isPlanPrint
    ? isComplianceFetching || isPlanReportFetching
    : isTeamMemberOldFetching || isTeamMemberXpFetching;

  const error = isPlanPrint
    ? (complianceError ?? planReportError)
    : (teamMemberOldError ?? teamMemberXpError);

  // Silently log errors
  useEffect(() => {
    if (error) {
      console.warn('[PrintReport] Fetch error:', error);
      datadogRum.addError(error);
    }
  }, [error]);

  // --- Report Generation ---
  const generateReport = async (showDoB: boolean, locations: string[]) => {
    if (!subject) return;

    if (isPlanPrint) {
      const reportData =
        isCompliance && !inHouseCompliancePlansFF
          ? complianceReportToPrint
          : buildNonComplianceReport(locations);

      const tableHead = [
        [
          Constants.REPORT_TABLE.NAME,
          ...(showDoB ? [Constants.REPORT_TABLE.DATE_OF_BIRTH] : []),
          ...(isCompliance
            ? [
                Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN,
                Constants.REPORT_TABLE.COMPLETION_DATE,
                Constants.REPORT_TABLE.EXPIRATION_DATE,
              ]
            : [
                Constants.REPORT_TABLE.ITEMS_COMPLETED,
                Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN,
                Constants.REPORT_TABLE.STATUS,
                Constants.REPORT_TABLE.COMPLETION_DATE,
              ]),
        ],
      ];

      const reportToGenerate = {
        category: t(mapCategoryToAliasTranslation(subject.plan.category ?? '')),
        foodSafetyImage:
          complianceReportData?.foodSafetyLetters?.[0]?.foodSafetyLetter
            ?.content,
        id: subject.plan.id,
        location: `${t('Generic.at')} ${
          !locations.length || printLocations.length < 2
            ? printLocations.toString()
            : arrayToCommaString(locations, t('Generic.and'))
        }`,
        name: itemByLanguage(subject.plan.name) ?? '',
        // tableHead always has one row, but keep this defensive for typing/lint safety
        reportToPrint: reportData.map(teamMember =>
          (tableHead[0] ?? []).map(headerItem => teamMember[headerItem]),
        ),
        tableHead,
      };

      // Lazy-load pdf generator to reduce initial bundle size
      const pdf = await import('./pdfGenerator');

      pdf.createPdfAndNotifyNative(reportToGenerate, reportData, tableHead);
    } else if (isTeamMemberPrint) {
      const statusReportToPrint = [
        ...xpPlans.map(mapXpStorePlanToReport),
        ...courses.map(mapComplianceCourseToReport),
      ].sort((planA, planB) => {
        const nameKey = Constants.REPORT_TABLE.PLAN;
        return (planA[nameKey] ?? '').localeCompare(planB[nameKey] ?? '');
      });

      const complianceReport = statusReportToPrint.filter(item =>
        (item[Constants.REPORT_TABLE.ITEMS_COMPLETED] ?? '').includes('%'),
      );

      const storeReport = statusReportToPrint.filter(
        item =>
          !(item[Constants.REPORT_TABLE.ITEMS_COMPLETED] ?? '').includes('%'),
      );

      const reportToGenerate = {
        category: t('Generic.teamMember'),
        isTeamMember: true,
        name: subject.name,
        complianceReport: complianceReport.map(course => [
          course[Constants.REPORT_TABLE.PLAN],
          course[Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN],
          course[Constants.REPORT_TABLE.COMPLETION_DATE],
          course[Constants.REPORT_TABLE.EXPIRATION_DATE],
        ]),
        complianceTableHead: Constants.COMPLIANCE_TABLE_HEAD,
        reportToPrint: storeReport.map(plan => [
          plan[Constants.REPORT_TABLE.PLAN],
          plan[Constants.REPORT_TABLE.ITEMS_COMPLETED],
          plan[Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN],
          plan[Constants.REPORT_TABLE.STATUS],
          plan[Constants.REPORT_TABLE.COMPLETION_DATE],
        ]),
        tableHead: Constants.TABLE_HEAD,
      };

      // Lazy-load pdf generator to reduce initial bundle size
      const pdf = await import('./pdfGenerator');

      pdf.createPdfAndNotifyNative(
        reportToGenerate,
        statusReportToPrint,
        Constants.TABLE_HEAD,
      );
    }
  };

  return {
    isFetching,
    generateReport,
    printLocations,
    startFetching,
  };
};
